import { AsyncPipe, CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnDestroy, Output } from "@angular/core";
import { FormBuilder, FormControl, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { DeviceType, isDeviceType } from "@domain/product/monitoring/device";
import { Family } from "@domain/product/monitoring/family/family";
import { StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { FamilyStepOption } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { CentralStationConfiguration } from "@domain/project/configurations/monitoring/central-station-configuration";
import { DisplayConfiguration } from "@domain/project/configurations/monitoring/display-configuration";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { TelemetryConfiguration } from "@domain/project/configurations/monitoring/telemetry-configuration";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ContentBoxComponent } from "@odx/angular/components/content-box";
import { RadioGroupComponent } from "@odx/angular/components/radio-group";
import { untilDestroyed } from "@odx/angular/utils";
import {
  FamilyStepMap,
  FamilyVariantMode,
  FamilyVariantResult,
  getFamilyVariantDeviceTypes,
  getMonitoringCategoryDataType,
  resolveCentralStationFamilySteps,
  resolveFamilyVariantStep,
  resolvePatientMonitorFamilySteps,
  resolveTelemetryFamilySteps,
} from "@project/monitoring/monitoring-family-wizard.utils";
import { MonitoringOptionCardComponent } from "@project/monitoring/monitoring-option-card/monitoring-option-card.component";
import { OptionDisplay, OptionDisplayService } from "@project/monitoring/option-display.service";
import { BehaviorSubject, combineLatest, forkJoin, map, Observable, of, ReplaySubject, switchMap } from "rxjs";

type FamilyStepResolver = (
  family: Family,
  familyVariant: FamilyVariantResult,
  deviceTypesByMatNr: ReadonlyMap<string, DeviceType>,
) => FamilyStepMap;

const FAMILY_STEP_RESOLVERS: Partial<Record<MonitoringCategory, FamilyStepResolver>> = {
  [MonitoringCategory.PATIENT_MONITORS]: resolvePatientMonitorFamilySteps,
  [MonitoringCategory.TELEMETRY]: resolveTelemetryFamilySteps,
  [MonitoringCategory.CENTRAL_STATIONS]: resolveCentralStationFamilySteps,
};

@Component({
  selector: "app-product-family-selector",
  imports: [
    AsyncPipe,
    ContentBoxComponent,
    FilterProductComponent,
    FormsModule,
    RadioGroupComponent,
    ReactiveFormsModule,
    MonitoringOptionCardComponent,
    RefreshProductsHintComponent,
    CommonModule,
  ],
  templateUrl: "./product-family-selector.component.html",
})
export class ProductFamilySelectorComponent implements OnDestroy {
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);
  private formBuilder = inject(FormBuilder);
  private optionDisplayService = inject(OptionDisplayService);

  @Input({ required: true }) set originalFamily(
    originalFamily:
      | PatientMonitorConfiguration
      | TelemetryConfiguration
      | DisplayConfiguration
      | CentralStationConfiguration
      | MonitoringCategory,
  ) {
    if (!(originalFamily instanceof ProductConfiguration)) {
      this.collectProductFamilies(originalFamily);
      return;
    } else if (originalFamily instanceof ProductConfiguration) {
      forkJoin({
        loadedFamilies: this.loadCategoryFamilies(originalFamily.category),
        restoredFamily: this.monitoringProductService.loadFamilyFromDB(
          originalFamily.category,
          originalFamily.familyNumber,
          this.productService.getFilterCountry(),
        ),
      }).subscribe(({ loadedFamilies, restoredFamily }) => {
        this.optionDisplays = loadedFamilies.optionDisplays;
        this.optionDisplays$.next(loadedFamilies.optionDisplays);
        this.families$.next(loadedFamilies.families);

        if (!restoredFamily) return;
        const renderedDisplay = loadedFamilies.optionDisplays.find(
          (display) => display.family?.familyNumber === restoredFamily.familyNumber,
        );
        if (!renderedDisplay) return;

        this.familyForm.patchValue({ selectedFamily: renderedDisplay });
        this.selectedFamily = renderedDisplay;
      });
    }
  }

  @Input() title!: string;
  @Input() category: MonitoringCategory = MonitoringCategory.PATIENT_MONITORS;
  @Input() categoryLabel?: string;

  @Output() familySelect$ = new EventEmitter<OptionDisplay | undefined>();

  protected familyForm = this.formBuilder.group({
    selectedFamily: new FormControl<null | OptionDisplay>(null, Validators.required),
  });

  @Output()
  formReady = of(this.familyForm);

  protected selectedFamily?: OptionDisplay;
  protected readonly optionDisplays$ = new ReplaySubject<OptionDisplay[]>(1);
  protected readonly filteredOptionDisplays$ = new ReplaySubject<OptionDisplay[]>(1);
  protected readonly StepSelectionType = StepSelectionType;
  private optionDisplays: OptionDisplay[] = [];

  private readonly takeUntilDestroyed = untilDestroyed();
  private readonly families$ = new ReplaySubject<Family[]>(1);
  private readonly searchValue$ = new BehaviorSubject<string | null>(null);
  constructor() {
    combineLatest([this.searchValue$, this.optionDisplays$]).subscribe(([searchValue, optionDisplays]) => {
      if (searchValue) {
        const filteredOptions = searchValue
          ? optionDisplays.filter((option) =>
              this.monitoringProductService.searchFilterFamily(option, searchValue, this.productService.getFilterCountry()),
            )
          : optionDisplays;
        this.filteredOptionDisplays$.next(filteredOptions);
      } else {
        this.filteredOptionDisplays$.next(optionDisplays);
      }
    });

    this.familyForm.valueChanges.pipe(this.takeUntilDestroyed()).subscribe(() => {
      const selectedFamily = this.familyForm.value.selectedFamily;
      if (!selectedFamily) {
        this.selectedFamily = undefined;
        this.familySelect$.emit(undefined);
        return;
      }

      this.selectedFamily = selectedFamily;
      this.familySelect$.emit(selectedFamily);
    });
  }

  ngOnDestroy() {
    this.families$.complete();
    this.optionDisplays$.complete();
    this.filteredOptionDisplays$.complete();
    this.searchValue$.complete();
  }

  protected search(value: string) {
    this.searchValue$.next(value);
  }

  protected select(selection: OptionDisplay) {
    const currentSelection = this.familyForm.value.selectedFamily;
    if (
      currentSelection &&
      currentSelection.family?.familyNumber &&
      selection.family?.familyNumber &&
      currentSelection.family.familyNumber === selection.family.familyNumber
    ) {
      return;
    }

    this.familyForm.patchValue({
      selectedFamily: selection,
    });
  }

  protected get refreshProductsDataType(): string | undefined {
    return getMonitoringCategoryDataType(this.category);
  }

  private collectProductFamilies(category: MonitoringCategory) {
    this.loadCategoryFamilies(category).subscribe(({ families, optionDisplays }) => {
      this.optionDisplays = optionDisplays;
      this.optionDisplays$.next(optionDisplays);
      this.families$.next(families);
    });
  }

  private loadCategoryFamilies(category: MonitoringCategory) {
    const variantDeviceTypes = getFamilyVariantDeviceTypes(category);

    return this.monitoringProductService.loadCategoryFamiliesFromDB(category, this.productService.getFilterCountry()).pipe(
      switchMap((families) => {
        if (families.length === 0) return of({ families: [], optionDisplays: [] });

        return forkJoin(families.map((family) => this.resolveFamilyVariant(family, category, variantDeviceTypes))).pipe(
          switchMap((familyVariants) =>
            forkJoin(
              families.map((family, index) =>
                this.createFamilyOptionDisplay(family, category, familyVariants[index]).pipe(
                  map((display) => ({ ...display, familyVariant: familyVariants[index] })),
                ),
              ),
            ).pipe(
              map((optionDisplays) => ({
                families: families.filter((_, index) => this.isFamilySelectable(category, familyVariants[index])),
                optionDisplays: optionDisplays.filter((_, index) => this.isFamilySelectable(category, familyVariants[index])),
              })),
            ),
          ),
        );
      }),
    );
  }

  private createFamilyOptionDisplay(
    family: Family,
    category: MonitoringCategory,
    familyVariant: FamilyVariantResult,
  ): Observable<OptionDisplay> {
    if ([FamilyVariantMode.MULTIPLE_OPTIONS, FamilyVariantMode.NO_OPTIONS].includes(familyVariant.variant.mode)) {
      return of(this.createFamilyMetadataDisplay(family));
    }

    const familyOption = family.steps[0]?.options?.[0];
    if (!familyOption) {
      return of(this.createFamilyMetadataDisplay(family));
    }

    return this.optionDisplayService.createOptionDisplay(familyOption, category, family);
  }

  private createFamilyMetadataDisplay(family: Family): OptionDisplay {
    const familyOption = new FamilyStepOption([], family.familyName, 1, 1);

    return {
      option: familyOption,
      displayName: family.familyName,
      imageId: family.familyImageCantoId ? ProductImage.getCantoSourceUrl(family.familyImageCantoId) : undefined,
      description: family.familyDescription,
      piSheetURL: family.familyPiSheetUrl,
      family,
    } satisfies OptionDisplay;
  }

  private isFamilySelectable(category: MonitoringCategory, familyVariant?: FamilyVariantResult) {
    return (
      !getFamilyVariantDeviceTypes(category) ||
      [MonitoringCategory.PATIENT_MONITORS, MonitoringCategory.TELEMETRY, MonitoringCategory.CENTRAL_STATIONS].includes(
        category,
      ) ||
      familyVariant?.variant.mode !== FamilyVariantMode.NO_OPTIONS
    );
  }

  private resolveFamilyVariant(
    family: Family,
    category: MonitoringCategory,
    variantDeviceTypes?: readonly DeviceType[],
  ): Observable<FamilyVariantResult> {
    if (!variantDeviceTypes) return of(resolveFamilyVariantStep(family));

    const familyOptions = family.steps.flatMap((step) => step.options ?? []);
    const deviceNumbers = [...new Set(familyOptions.flatMap((option) => option.devices))];

    return forkJoin(
      deviceNumbers.map((deviceNumber) =>
        this.monitoringProductService.loadDeviceFromDB(deviceNumber, category, this.productService.getFilterCountry()),
      ),
    ).pipe(
      map((devices) => {
        const deviceTypesByMatNr = new Map<string, DeviceType>();
        devices.forEach((device) => {
          if (device && isDeviceType(device.deviceType)) deviceTypesByMatNr.set(device.matNr, device.deviceType);
        });

        const familyVariant = resolveFamilyVariantStep(family, {
          variantDeviceTypes,
          deviceTypesByMatNr,
        });
        return {
          ...familyVariant,
          steps: FAMILY_STEP_RESOLVERS[category]?.(family, familyVariant, deviceTypesByMatNr),
        };
      }),
    );
  }
}
